import { useEffect, useState } from "react";
import { toast } from "sonner";
import PortalLayout from "@/components/portal/PortalLayout";
import { StatusBadge } from "@/pages/ClientDashboard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PencilSimple, Trash } from "@phosphor-icons/react";

const ENQUIRY_STATUSES = ["New", "Contacted", "Quoted", "Closed"];
const FILING_STATUSES = ["Documents Pending", "Filed", "Under Examination", "Published", "Opposition", "Granted", "Registered", "Renewed"];
const REQUEST_STATUSES = ["Pending", "Approved", "Declined"];

const inputCls = "bg-navy-950 border border-white/10 focus:border-gold outline-none px-3 py-2.5 text-white placeholder:text-mist/40 transition-colors duration-300 text-sm w-full";

const Field = ({ label, children }) => (
  <div>
    <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-1">{label}</p>
    <div className="text-mist">{children}</div>
  </div>
);

const enquiryServices = (e) =>
  Array.isArray(e.services) && e.services.length ? e.services.join(", ") : e.service || "—";

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState([]);
  const [filings, setFilings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editClient, setEditClient] = useState(null);
  const [form, setForm] = useState({ client_email: "", type: "Patent", title: "", jurisdiction: "", reference: "", status: "Filed" });

  const load = () => {
    api.get("/enquiries").then((r) => setEnquiries(r.data)).catch(() => {});
    api.get("/filings").then((r) => setFilings(r.data)).catch(() => {});
    api.get("/service-requests").then((r) => setRequests(r.data)).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const setEnquiryStatus = async (id, status) => {
    try {
      await api.patch(`/enquiries/${id}/status`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
      setSelected((s) => (s && s.id === id ? { ...s, status } : s));
      toast.success("Enquiry updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setFilingStatus = async (id, status) => {
    try {
      await api.patch(`/filings/${id}/status`, { status });
      setFilings((list) => list.map((f) => (f.id === id ? { ...f, status } : f)));
      toast.success("Filing status updated — client notified by email");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteEnquiry = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      setSelected(null);
      toast.success("Enquiry deleted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setRequestStatus = async (id, status) => {
    try {
      await api.patch(`/service-requests/${id}/status`, { status });
      setRequests((list) => list.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Request ${status.toLowerCase()}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveClient = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/clients/${editClient.id}`, {
        name: editClient.name,
        company: editClient.company || "",
        phone: editClient.phone || "",
        notes: editClient.notes || "",
      });
      setClients((list) => list.map((c) => (c.id === data.id ? { ...c, ...data } : c)));
      setEditClient(null);
      toast.success("Client profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addFiling = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/filings", form);
      setFilings((list) => [data, ...list]);
      setForm({ client_email: "", type: "Patent", title: "", jurisdiction: "", reference: "", status: "Filed" });
      toast.success("Filing added to client account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <PortalLayout title="Enquiries & Filings" subtitle="Every quote request, service request, client and filing — in one place. Click any enquiry to read the full message.">
      <Tabs defaultValue="enquiries" data-testid="admin-tabs">
        <TabsList className="bg-navy-900 border border-white/10 mb-8 flex-wrap h-auto">
          <TabsTrigger value="enquiries" data-testid="admin-tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          <TabsTrigger value="requests" data-testid="admin-tab-requests">Requests ({requests.filter((r) => r.status === "Pending").length} pending)</TabsTrigger>
          <TabsTrigger value="clients" data-testid="admin-tab-clients">Clients ({clients.length})</TabsTrigger>
          <TabsTrigger value="filings" data-testid="admin-tab-filings">Filings ({filings.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="enquiries">
          <div className="bg-navy-900 border border-white/10 overflow-x-auto">
            {enquiries.length === 0 ? (
              <p className="p-8 text-mist text-sm" data-testid="enquiries-empty">No enquiries yet. New website quote requests will appear here instantly.</p>
            ) : (
              <Table data-testid="enquiries-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {["Received", "Name", "Email", "Services", "Jurisdiction", "Message", "Status"].map((h) => (
                      <TableHead key={h} className="text-gold/80 uppercase text-xs tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.map((e) => (
                    <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`} onClick={() => setSelected(e)}
                      className="border-white/5 hover:bg-navy-800/40 cursor-pointer">
                      <TableCell className="text-mist text-xs whitespace-nowrap">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-white text-sm">{e.name}{e.company ? <span className="block text-xs text-mist">{e.company}</span> : null}</TableCell>
                      <TableCell className="text-mist text-sm">{e.email}</TableCell>
                      <TableCell className="text-mist text-sm max-w-[200px] truncate">{enquiryServices(e)}</TableCell>
                      <TableCell className="text-mist text-sm">{e.jurisdiction || "—"}</TableCell>
                      <TableCell className="text-mist text-sm max-w-[220px]">
                        <span className="block truncate" data-testid={`enquiry-message-${e.id}`}>{e.message}</span>
                        <span className="text-[10px] uppercase tracking-wider text-gold/60">Click to read</span>
                      </TableCell>
                      <TableCell onClick={(ev) => ev.stopPropagation()}>
                        <Select value={e.status} onValueChange={(v) => setEnquiryStatus(e.id, v)}>
                          <SelectTrigger data-testid={`enquiry-status-${e.id}`} className="w-32 bg-navy-950 border-white/10 text-xs h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-navy-900 border-gold/30 text-white">
                            {ENQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="requests">
          <div className="bg-navy-900 border border-white/10 overflow-x-auto">
            {requests.length === 0 ? (
              <p className="p-8 text-mist text-sm" data-testid="requests-empty">No service requests yet. When clients request additional services from their portal, they appear here for your approval.</p>
            ) : (
              <Table data-testid="requests-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {["Received", "Client", "Services requested", "Note", "Decision"].map((h) => (
                      <TableHead key={h} className="text-gold/80 uppercase text-xs tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {requests.map((r) => (
                    <TableRow key={r.id} data-testid={`admin-request-row-${r.id}`} className="border-white/5 hover:bg-navy-800/40">
                      <TableCell className="text-mist text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</TableCell>
                      <TableCell className="text-white text-sm">{r.client_name}<span className="block text-xs text-mist">{r.client_email}</span></TableCell>
                      <TableCell className="text-mist text-sm">{r.services.join(", ")}</TableCell>
                      <TableCell className="text-mist text-sm max-w-[220px] truncate">{r.note || "—"}</TableCell>
                      <TableCell>
                        <Select value={r.status} onValueChange={(v) => setRequestStatus(r.id, v)}>
                          <SelectTrigger data-testid={`request-status-${r.id}`} className="w-32 bg-navy-950 border-white/10 text-xs h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-navy-900 border-gold/30 text-white">
                            {REQUEST_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="clients">
          <div className="bg-navy-900 border border-white/10 overflow-x-auto">
            {clients.length === 0 ? (
              <p className="p-8 text-mist text-sm" data-testid="clients-empty">No registered clients yet.</p>
            ) : (
              <Table data-testid="clients-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {["Name", "Email", "Company", "Phone", "Filings", "Requests", ""].map((h) => (
                      <TableHead key={h} className="text-gold/80 uppercase text-xs tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((c) => (
                    <TableRow key={c.id} data-testid={`client-row-${c.id}`} className="border-white/5 hover:bg-navy-800/40">
                      <TableCell className="text-white text-sm">{c.name}</TableCell>
                      <TableCell className="text-mist text-sm">{c.email}</TableCell>
                      <TableCell className="text-mist text-sm">{c.company || "—"}</TableCell>
                      <TableCell className="text-mist text-sm">{c.phone || "—"}</TableCell>
                      <TableCell className="text-gold text-sm">{c.filings_count}</TableCell>
                      <TableCell className="text-gold text-sm">{c.requests_count}</TableCell>
                      <TableCell>
                        <button data-testid={`client-edit-${c.id}`} onClick={() => setEditClient({ ...c })}
                          className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-navy-950 transition-colors">
                          <PencilSimple size={14} /> Edit
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="filings">
          <form onSubmit={addFiling} data-testid="add-filing-form" className="bg-navy-900 border border-gold/20 p-6 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <input required type="email" data-testid="filing-client-email" placeholder="Client email *" value={form.client_email}
              onChange={(e) => setForm({ ...form, client_email: e.target.value })} className={inputCls} />
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="filing-type-select" className="bg-navy-950 border-white/10 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-navy-900 border-gold/30 text-white">
                <SelectItem value="Patent">Patent</SelectItem>
                <SelectItem value="Trademark">Trademark</SelectItem>
              </SelectContent>
            </Select>
            <input required data-testid="filing-title" placeholder="Title (e.g. AURELIA - Word Mark) *" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
            <input data-testid="filing-jurisdiction" placeholder="Jurisdiction (e.g. USPTO)" value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} className={inputCls} />
            <input data-testid="filing-reference" placeholder="Reference (e.g. CS-TM-1044)" value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })} className={inputCls} />
            <button type="submit" data-testid="filing-add-button"
              className="px-6 py-2.5 bg-gold text-navy-950 font-semibold text-sm hover:bg-gold-light transition-colors">
              Add Filing
            </button>
          </form>

          <div className="bg-navy-900 border border-white/10 overflow-x-auto">
            {filings.length === 0 ? (
              <p className="p-8 text-mist text-sm" data-testid="admin-filings-empty">No filings yet. Add one above to attach it to a client account.</p>
            ) : (
              <Table data-testid="admin-filings-table">
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    {["Client", "Reference", "Type", "Title", "Jurisdiction", "Status"].map((h) => (
                      <TableHead key={h} className="text-gold/80 uppercase text-xs tracking-wider">{h}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filings.map((f) => (
                    <TableRow key={f.id} data-testid={`admin-filing-row-${f.id}`} className="border-white/5 hover:bg-navy-800/40">
                      <TableCell className="text-mist text-sm">{f.client_email}</TableCell>
                      <TableCell className="text-gold text-sm">{f.reference || "—"}</TableCell>
                      <TableCell className="text-white text-sm">{f.type}</TableCell>
                      <TableCell className="text-mist text-sm max-w-[220px] truncate">{f.title}</TableCell>
                      <TableCell className="text-mist text-sm">{f.jurisdiction || "—"}</TableCell>
                      <TableCell>
                        <Select value={f.status} onValueChange={(v) => setFilingStatus(f.id, v)}>
                          <SelectTrigger data-testid={`filing-status-${f.id}`} className="w-44 bg-navy-950 border-white/10 text-xs h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-navy-900 border-gold/30 text-white">
                            {FILING_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent data-testid="enquiry-detail-dialog" className="bg-navy-900 border-gold/30 text-white sm:max-w-xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-white font-light">{selected.name}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <Field label="Email">
                  <a href={`mailto:${selected.email}`} data-testid="enquiry-detail-email" className="text-gold hover:text-gold-light transition-colors">{selected.email}</a>
                </Field>
                <Field label="Company">{selected.company || "—"}</Field>
                <Field label="Services">{enquiryServices(selected)}</Field>
                <Field label="Jurisdiction">{selected.jurisdiction || "—"}</Field>
                <Field label="Received">{new Date(selected.created_at).toLocaleString()}</Field>
                <Field label="Status">{selected.status}</Field>
              </div>
              <div className="mt-2 border-t border-gold/20 pt-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-3">Full message</p>
                <p data-testid="enquiry-detail-message" className="text-mist leading-relaxed whitespace-pre-wrap">{selected.message}</p>
              </div>
              <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold">Update status</p>
                  <Select value={selected.status} onValueChange={(v) => setEnquiryStatus(selected.id, v)}>
                    <SelectTrigger data-testid="enquiry-detail-status" className="w-40 bg-navy-950 border-white/10 text-xs h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-900 border-gold/30 text-white">
                      {ENQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <button type="button" data-testid="enquiry-delete-button" onClick={() => deleteEnquiry(selected.id)}
                  className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-red-400/40 text-red-300 hover:bg-red-400/10 transition-colors">
                  <Trash size={14} /> Delete enquiry
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editClient} onOpenChange={(open) => !open && setEditClient(null)}>
        <DialogContent data-testid="client-edit-dialog" className="bg-navy-900 border-gold/30 text-white sm:max-w-lg">
          {editClient && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-3xl text-white font-light">Edit client</DialogTitle>
              </DialogHeader>
              <form onSubmit={saveClient} className="flex flex-col gap-4 mt-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-gold font-semibold mb-2">Email (login)</p>
                  <p className="text-mist text-sm">{editClient.email}</p>
                </div>
                <input required data-testid="client-edit-name" placeholder="Full name" value={editClient.name}
                  onChange={(e) => setEditClient({ ...editClient, name: e.target.value })} className={inputCls} />
                <input data-testid="client-edit-company" placeholder="Company" value={editClient.company || ""}
                  onChange={(e) => setEditClient({ ...editClient, company: e.target.value })} className={inputCls} />
                <input data-testid="client-edit-phone" placeholder="Phone" value={editClient.phone || ""}
                  onChange={(e) => setEditClient({ ...editClient, phone: e.target.value })} className={inputCls} />
                <textarea rows={3} data-testid="client-edit-notes" placeholder="Internal notes (payment terms, discussions...)" value={editClient.notes || ""}
                  onChange={(e) => setEditClient({ ...editClient, notes: e.target.value })} className={`${inputCls} resize-none`} />
                <button type="submit" data-testid="client-edit-save"
                  className="mt-2 px-6 py-3 bg-gold text-navy-950 font-semibold text-sm hover:bg-gold-light transition-colors">
                  Save Changes
                </button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </PortalLayout>
  );
}
